/**
 * @author        woody@zumper.com (Woody Anderson)
 * @copyright     Copyright (c) 2016, Padmapper
 * @description   Model representing Building Entity.
 */

import { Agent, Fees, Hours, PetPolicy, SupplementalContent, autoclass } from '@zcode/bizos'
import { PropertyFeatures } from '@zcode/enums'
import { mediaUrl, toBuildingShortUrl } from '@zcode/nr-utils'
import { isNumber } from '@zcode/utils'
import { BOZZUTO_ATTRIBUTION_BUILDING_IDS, DOMINIUM_BROKERAGE_ID } from '../constants/source'
import { constructUrl } from '../packages/imgix'
import { pageTitleBuildingDetails } from './helpers/pageTitleBuildingDetails'
import Listable from './listable'
import Listing, { scrub as scrubListing } from './listing'
import Media, { Size } from './media'
import { MNeighborhood, scrub as scrubNeighborhood } from './neighborhood'
import Rentable from './rentable'
import * as RentableUtils from './rentable_utils'

/**
 * @type {{
  new <Data extends import('@zcode/state-type').FloorplanListingData>(
    d?: Data
  ): Readonly<import('@zcode/state-type').FloorplanListing<Data>> & import('./base').Bizo<import('@zcode/state-type').FloorplanListing<Data>, Data>
  static fromJS: (
      <Data extends import('@zcode/state-type').FloorplanListingData>(
        data: Data
      ) => Readonly<import('@zcode/state-type').FloorplanListing<Data>> & import('./base').Bizo<import('@zcode/state-type').FloorplanListing<Data>, Data>
    ) &
    ((data?: undefined) => undefined) &
    (<T extends 0 | 0n | false | '' | null>(data?: T) => T)
}}
 */
export const Floorplan = autoclass('Floorplan', class Floorplan extends Listing {}, {
  isFloorplan: () => true,
})

export const scrub = (data) => {
  if (!data) {
    return data
  }
  const scrubbedData = { ...data }
  if (scrubbedData.neighborhood !== undefined) {
    scrubbedData.neighborhood = scrubNeighborhood(scrubbedData.neighborhood)
  }
  if (scrubbedData.floorplan_listings !== undefined) {
    scrubbedData.floorplan_listings = scrubbedData.floorplan_listings.map(scrubListing)
  }
  return scrubbedData
}

/**
 * @type {import('./base').RealBizoType<import('@zcode/state-type').Building, object>}
 */
const Building = autoclass(
  'Building',
  class Building extends Rentable {
    constructor(data = {}) {
      super(data)
      this._data = scrub(data)
    }
  },
  {
    id: 'building_id',
    status: 'building_status',
    activeOn: 'active_on',
    address: 'address',
    airbnbUrl: 'airbnb_url',
    agentId: 'agent_id',
    amenities: 'amenities',
    amenityTags: 'amenity_tags',
    attribution: 'attribution',
    attributionLink: 'homepage',
    averagePrice: 'average_price',
    averageSquareFeet: 'average_square_feet',
    brokerageId: 'brokerage_id',
    brokerageName: 'brokerage_name',
    buildingId: 'building_id',
    buildingStatus: 'building_status',
    buildingLabel: 'building_label',
    cityState: 'city_state',
    country: 'country',
    createdOn: 'created_on',
    data: 'data',
    deposits: 'deposits',
    description: 'description',
    displayAddress: 'display_address',
    external: 'external',
    feedName: 'feed_name',
    fees: { name: 'fees', type: Fees, isArray: true },
    floors: 'floors',
    formattedAddress: 'formatted_address',
    homepage: 'homepage',
    hours: { name: 'hours', type: Hours, isArray: true },
    house: 'house',
    imageIds: 'image_ids',
    incomeRestrictions: 'income_restrictions',
    intersection: 'intersection',
    isFilterAd: 'is_filter_ad',
    isMessageable: 'is_messageable',
    lat: 'lat',
    leaseTerms: 'lease_terms',
    leaseType: 'lease_type',
    leasingFee: 'leasing_fee',
    lng: 'lng',
    maxLeaseDays: 'max_lease_days',
    minLeaseDays: 'min_lease_days',
    modifiedOn: 'modified_on',
    name: 'name',
    neighborhoodId: 'neighborhood_id',
    neighborhoodName: 'neighborhood_name',
    parkingData: 'parking_data',
    paShouldIndex: 'pa_should_index',
    paUrl: 'pa_url',
    parsedData: 'parsed_data',
    pbId: 'pb_id',
    pbUrl: 'pb_url',
    petPolicy: { name: 'pet_policy', type: PetPolicy, isArray: true },
    phone: 'phone',
    providerUid: 'provider_id',
    providerUrl: 'homepage',
    shouldIndex: 'should_index',
    specials: 'specials',
    supplementalContent: { name: 'supplemental_content', type: SupplementalContent },
    state: 'state',
    street: 'street',
    tz: 'tz',
    unit: 'unit',
    units: 'units',
    url: 'url',
    yearBuilt: 'year_built',
    yearRemodeled: 'year_remodeled',
    zappable: 'zappable',
    zipcode: 'zipcode',
    agent: { name: ['agents', 0], type: Agent },
    floorplans: { name: 'floorplan_listings', type: Floorplan, isArray: true },
    media: { name: 'media', type: Media, isArray: true },
    neighborhood: { name: 'neighborhood', type: MNeighborhood },
    similar: { name: 'similar', type: Listable, isArray: true },
    neighborhoodUrl: ['neighborhood', 'url'],
    isFull: () => true,
    city: (t) => (t.neighborhood ? t.neighborhood.city : t._data.city),
    providerId: (t) => RentableUtils.parseProviderIdentifier(t._data.provider_id),
    providerIdOrigin: 'provider_id',
    providerSource: (t) => RentableUtils.parseProviderSource(t._data.provider_id),
    unitAmenities: (t) => {
      const amenities = []
      const floorplans =
        t.activeFloorplans && t.activeFloorplans.length ? t.activeFloorplans : t.floorplans
      if (floorplans && floorplans.length) {
        floorplans.reduce((_, f) => amenities.push.apply(amenities, f.amenities), amenities)
      }
      return Array.from(new Set(amenities))
    },
    unitAmenityTags: (t) => {
      const amenities = []
      const floorplans =
        t.activeFloorplans && t.activeFloorplans.length ? t.activeFloorplans : t.floorplans
      if (floorplans && floorplans.length) {
        floorplans.reduce((_, f) => amenities.push.apply(amenities, f.amenityTags), amenities)
      }
      return Array.from(new Set(amenities))
    },
    unitAmenityGroups: (t) => {
      const amenities = {}
      const floorplans = t.activeFloorplans?.length ? t.activeFloorplans : t.floorplans
      if (floorplans?.length) {
        floorplans.forEach((fp) => {
          Object.assign(amenities, fp.amenityGroups)
        })
      }
      return amenities
    },
    floorplansByStatus: (t) => {
      const fps = {}
      if (t.floorplans) {
        t.floorplans.forEach((f) => {
          fps[f.bedrooms] = fps[f.bedrooms] || { available: [], unavailable: [] }
          fps[f.bedrooms][f.isActive ? 'available' : 'unavailable'].push(f)
        })
      }
      return fps
    },
    similarAreAllBuildings: (t) => {
      return !t.similar || t.similar.every((s) => s.buildingId !== undefined)
    },
    uId: (t) => (t.pbId ? `b-p${t.pbId}` : `b-${t.buildingId}`),
    shortUrl: (t) => toBuildingShortUrl(t.buildingId),
    pageTitle: pageTitleBuildingDetails,
    /**
     * See https://github.com/zumper/zumper_code/issues/14046
     *
     * - `pets: null` means "we don't know whether pets are allowed"
     * - `pets: []` means "no pets are allowed"
     *
     * @param {import('@zcode/state-type').Building} t
     */
    pets: (t) => {
      const floorplansKey = t.isActive ? 'activeFloorplans' : 'floorplans'
      if (t[floorplansKey]?.length) {
        return t[floorplansKey].reduce(
          (/** @type {number[] | null} */ pets, f) =>
            f.pets ? (pets ? Array.from(new Set([...pets, ...f.pets])) : f.pets.slice()) : pets,
          null
        )
      }
      return null
    },
    activeFloorplans: (t) => (t.floorplans ? t.floorplans.filter((f) => f.isActive) : []),
    activeUnitsCount: (t) => {
      let unitsCount = 0
      if (Array.isArray(t.activeFloorplans)) {
        t.activeFloorplans.forEach((fp) => {
          const { activeUnits = 0 } = fp
          unitsCount += activeUnits
        })
      }
      return unitsCount
    },
    activeBedroomNumCategories: (t) => {
      const activeNumCategories = new Set()
      t.activeFloorplans.forEach((fp) => {
        if (isNumber(fp.bedrooms)) {
          activeNumCategories.add(fp.bedrooms)
        }
      })
      return Array.from(activeNumCategories).sort()
    },
    activeFpAvgPriceByBedroom: (t) => {
      const avgPricesByBedroom = []
      for (let a = 0; a <= 4; a++) {
        const activeFpByCurrBeds = t.activeFloorplans.filter(
          (fp) => fp?.bedrooms === a && fp?.price
        )
        const avgPriceByCurrBeds =
          activeFpByCurrBeds.length > 1
            ? activeFpByCurrBeds.reduce((a, b) => a.price + b.price) / activeFpByCurrBeds.length
            : null
        avgPricesByBedroom.push(
          activeFpByCurrBeds.length === 1
            ? activeFpByCurrBeds[0]?.price || null
            : avgPriceByCurrBeds
        )
      }
      return avgPricesByBedroom
    },
    floorplanCount: (t) => (t.floorplans ? t.floorplans.length : 0),
    activeFloorplanCount: (t) => t.activeFloorplans.length,
    minPrice: (t) => RentableUtils.minValue(t.activeFloorplans, 'minPrice'),
    maxPrice: (t) => RentableUtils.maxValue(t.activeFloorplans, 'maxPrice'),
    // only use active floorplan data if building is active
    minBedrooms: (t) =>
      t.isActive ? RentableUtils.minValue(t.activeFloorplans, 'bedrooms') : t.minAllBedrooms,
    maxBedrooms: (t) =>
      t.isActive ? RentableUtils.maxValue(t.activeFloorplans, 'bedrooms') : t.maxAllBedrooms,
    minSquareFeet: (t) =>
      t.isActive ? RentableUtils.minValue(t.activeFloorplans, 'minSquareFeet') : t.minAllSquareFeet,
    maxSquareFeet: (t) =>
      t.isActive ? RentableUtils.maxValue(t.activeFloorplans, 'maxSquareFeet') : t.maxAllSquareFeet,
    minBathrooms: (t) =>
      t.isActive ? RentableUtils.minValue(t.activeFloorplans, 'bathrooms') : t.minAllBathrooms,
    maxBathrooms: (t) =>
      t.isActive ? RentableUtils.maxValue(t.activeFloorplans, 'bathrooms') : t.maxAllBathrooms,
    // any/all floorplans
    minAllPrice: (t) => RentableUtils.minValue(t.floorplans, 'minPrice'),
    maxAllPrice: (t) => RentableUtils.maxValue(t.floorplans, 'maxPrice'),
    minAllBedrooms: (t) =>
      RentableUtils.minValue(RentableUtils.getNonRoomFloorplans(t.floorplans), 'bedrooms'),
    maxAllBedrooms: (t) =>
      RentableUtils.maxValue(RentableUtils.getNonRoomFloorplans(t.floorplans), 'bedrooms'),
    minAllSquareFeet: (t) => RentableUtils.minValue(t.floorplans, 'minSquareFeet'),
    maxAllSquareFeet: (t) => RentableUtils.maxValue(t.floorplans, 'maxSquareFeet'),
    minAllBathrooms: (t) => RentableUtils.minValue(t.floorplans, 'bathrooms'),
    maxAllBathrooms: (t) => RentableUtils.maxValue(t.floorplans, 'bathrooms'),
    isZappable: (t) => RentableUtils.isZappable(t),
    /**
     *
     * @param {import('@zcode/state-type').Building} t
     */
    bedroomRangeTextUnlabeled: (t) => {
      const { minBedrooms, maxBedrooms, floorplanCount } = t
      if (!floorplanCount) {
        return '\u2013'
      }
      const minText = minBedrooms === -1 ? 'Rooms' : minBedrooms || 'Studios'
      if (minBedrooms === maxBedrooms) {
        if (minBedrooms <= 0) {
          return minText
        }
        return `${minText}`
      }
      const maxText = maxBedrooms || 'Studios'
      return `${minText}\u2013${maxText}`
    },
    bedsRangeTxt: (t) => {
      const { minBedrooms, maxBedrooms, floorplanCount } = t
      if (!floorplanCount) {
        return '\u2014'
      }
      const minText = minBedrooms === -1 ? 'Rooms' : minBedrooms || 'Studios'
      if (minBedrooms === maxBedrooms) {
        if (minBedrooms <= 0) {
          return minText
        }
        return `${minText} Bed${minBedrooms === 1 ? '' : 's'}`
      }
      const maxText = maxBedrooms || 'Studios'
      return `${minText} - ${maxText}${maxBedrooms ? ` Bed${maxBedrooms === 1 ? '' : 's'}` : ''}`
    },
    bathsRangeTxt: (t) => {
      const { minBathrooms, maxBathrooms, floorplanCount } = t
      if (!floorplanCount) {
        return '\u2014'
      }
      if (minBathrooms === maxBathrooms) {
        return `${minBathrooms} Bath${minBathrooms === 1 ? '' : 's'}`
      }
      return `${minBathrooms} - ${maxBathrooms} Bath${maxBathrooms === 1 ? '' : 's'}`
    },
    /**
     *
     * @param {import('@zcode/state-type').Building} t
     */
    sqftRangeTextUnlabeled: (t) => {
      const { minSquareFeet, maxSquareFeet, floorplanCount } = t
      if (!floorplanCount || !minSquareFeet || !maxSquareFeet) {
        return ''
      }
      if (minSquareFeet === maxSquareFeet) {
        return RentableUtils.displaySqFt(minSquareFeet).split(' ')[0]
      }
      return [
        RentableUtils.displaySqFt(minSquareFeet).split(' ')[0],
        RentableUtils.displaySqFt(maxSquareFeet).split(' ')[0],
      ].join('\u2013')
    },
    sqftRangeTxt: (t) => {
      const { minSquareFeet, maxSquareFeet, floorplanCount } = t
      if (!floorplanCount || !minSquareFeet || !maxSquareFeet) {
        return '\u2014'
      }
      if (minSquareFeet === maxSquareFeet) {
        return `${minSquareFeet} sqft`
      }
      return `${minSquareFeet} - ${maxSquareFeet} sqft`
    },
    isBozzuto: (t) => BOZZUTO_ATTRIBUTION_BUILDING_IDS.includes(t.buildingId),
    isDominium: (t) => {
      const { brokerageId } = t
      return brokerageId === DOMINIUM_BROKERAGE_ID
    },
    /**
     *
     * @param {import('@zcode/state-type').Building} t
     */
    waitlistEnabled: ({ floorplans }) =>
      !!floorplans.length && floorplans.every((floorplan) => floorplan.waitlistEnabled),
    transparentCompanyLogo: (t) => {
      const imageId = t?.parsedData?.custom_branding?.transparent_company_logo
      if (imageId) {
        return {
          id: imageId,
          url: constructUrl(mediaUrl(imageId, Size.XLARGE), { h: 30, fit: 'clip' }),
        }
      }
      return null
    },
    isZumperRentals: (t) => {
      return !!t?.features?.includes(PropertyFeatures.ZUMPERRENTALS)
    },
  }
)
export default Building
